/* eslint-disable  */
import { PropsWithChildren } from 'react'
import {
  createTheme,
  CssBaseline,
  ThemeProvider as MuiThemeProvider,
  Theme,
  PaletteMode,
  ScopedCssBaseline,
} from '@mui/material'

import { rawThemeCurrent, useDeviceDetect, ThemeDeviceProps } from '@dhl/theme'

import {
  rawThemeNew,
  ColorSchemeProvider,
  useColorScheme,
  ThemeOption,
  useAutoDetectThemeVariant,
  ThemeOptionVariant,
  UseThemeProvider,
  TenantThemeProvider as DhlTenantThemeProvider,
  TenantId,
  TenantThemeConfig,
} from '@dhl-ui/components'

type ThemeOrFunc = Theme | ((theme: Theme) => Theme) | undefined

/**
 *
 */
type DhlThemeOptions = {
  device?: ThemeDeviceProps
  disableRipple?: boolean
} & Partial<Pick<ThemeOption, 'variant' | 'disablePtr' | 'disableActiveState' | 'userSelect'>>

export type ThemeProviderProps = {
  theme?: ThemeOrFunc

  /**
   * Use generic css reset, can be usefull in shared applications like our locations widget
   * @default true
   */
  useCssBaseline?: boolean

  options?: DhlThemeOptions
}

const determineTheme = (
  paletteMode: PaletteMode,
  theme: ThemeOrFunc,
  options: DhlThemeOptions,
  variant: ThemeOptionVariant
): Theme => {
  // our "old" theme
  const currentTheme = createTheme(rawThemeCurrent(paletteMode, options))

  const empty = { styleOverrides: {} }

  const pickedCurrentTheme = {
    ...currentTheme,
    components: {
      ...currentTheme.components,
      // whipe out definitons where we are sure we rule these now
      // we make sure we are backwards compatible, when we change defaults we can not do this
      MuiAlert: empty, // backwards compatible
      MuiAvatar: empty,
      MuiBackdrop: empty,
      MuiBottomNavigation: empty,
      MuiBottomNavigationAction: empty,
      MuiButton: empty, // TODO: not backwards compatible
      MuiButtonBase: empty,
      MuiButtonGroup: empty,
      MuiCheckbox: empty,
      // MuiCssBaseline
      MuiDataGrid: empty,
      MuiDivider: empty,
      MuiFilledInput: empty,
      MuiFormControl: empty,
      MuiFormLabel: empty,
      MuiIconButton: empty,
      MuiInputBase: empty,
      MuiInputLabel: empty,
      MuiLink: empty,
      MuiMenuItem: empty,
      MuiOutlinedInput: empty,
      MuiPaper: empty,
      MuiRadio: empty,
      MuiSelect: empty,
      // MuiSnackbarContent
      MuiSvgIcon: empty,
      MuiSwitch: empty,
      MuiTab: empty,
      MuiTabs: empty,
      MuiTableCell: empty,
      MuiTableHead: empty,
      MuiTableRow: empty,
      MuiTableBody: empty,
      MuiTableFooter: empty,
      MuiToolbar: empty,
      MuiTooltip: empty,
      MuiToggleButton: empty,

      // not sure
      // MuiFilledInput
      // MuiOutlinedInput
    },
    zIndex: undefined,
  }

  const newTheme = rawThemeNew(paletteMode, variant, {
    userSelect: options.userSelect,
    disablePtr: options.disablePtr,
    disableActiveState: options.disableActiveState,
  })

  // our "new" theme extended on the old one
  const baseTheme = createTheme(pickedCurrentTheme, newTheme)

  const mergedTheme = {
    ...baseTheme,
    typography: {
      // we use the old typography since for new stuff we want our Body1/H1/etc
      // then apps who have old and new together will not get regression on stuff like old Modals etc etc.
      ...pickedCurrentTheme.typography,
      // body1 and button are used over buttons/tabs/inputs etc they seem
      // to give same sizes on mixed old/new connector apps.
      body1: baseTheme.typography.body1,
      button: baseTheme.typography.button,
    },
  }

  /**
   * Consumers in the current applications could extend on Theme themselves
   * In the future we want to have empty extensions, but this gives them backwards compatibility
   */
  if (typeof theme === 'function') {
    const extendedTheme = theme(mergedTheme)

    if (extendedTheme.typography) {
      // reason: we need to tell our consumers they extended typography which will be gone so they need to take action !!
      console.log('removed extended typography!')
    }

    // TODO: remove component defintions when we have set these
    // TEST: locker/ self-scan/ sorter-adapter
    extendedTheme.typography = baseTheme.typography

    return extendedTheme
  }

  return theme ?? mergedTheme
}

const ThemeProviderComponent = ({
  children,
  theme,
  useCssBaseline = true,
  options,
}: PropsWithChildren<ThemeProviderProps>) => {
  const { activePaletteMode } = useColorScheme()
  const defaultDevice = useDeviceDetect()
  const variant = useAutoDetectThemeVariant(options?.variant)

  const determinedTheme = determineTheme(
    activePaletteMode,
    theme,
    {
      ...options,
      device: options?.device || defaultDevice,
    },
    variant
  )

  if (!useCssBaseline) {
    return (
      <UseThemeProvider theme={determinedTheme}>
        <MuiThemeProvider theme={determinedTheme}>
          <ScopedCssBaseline>{children}</ScopedCssBaseline>
        </MuiThemeProvider>
      </UseThemeProvider>
    )
  }

  return (
    <UseThemeProvider theme={determinedTheme}>
      <MuiThemeProvider theme={determinedTheme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </UseThemeProvider>
  )
}

/**
 * This ThemeProvider is when you already want to use the new setup providing our DHL theme
 */
export const ThemeProvider = ({ theme, children, ...rest }: PropsWithChildren<ThemeProviderProps>) => (
  <ColorSchemeProvider>
    <ThemeProviderComponent theme={theme} {...rest}>
      {children}
    </ThemeProviderComponent>
  </ColorSchemeProvider>
)

/**
 * Keeps connector `theme` compatibility when tenant mode is enabled.
 */
const resolveTenantOverrides = (
  tenantOverrides: Partial<TenantThemeConfig> | undefined,
  theme: ThemeOrFunc
): Partial<TenantThemeConfig> | undefined => {
  if (!theme) {
    return tenantOverrides
  }

  const previousOverrideTheme = tenantOverrides?.theme

  if (typeof theme === 'function') {
    return {
      ...tenantOverrides,
      theme: (baseTheme) => {
        const tenantBaseTheme =
          typeof previousOverrideTheme === 'function'
            ? previousOverrideTheme(baseTheme)
            : (previousOverrideTheme ?? baseTheme)

        return theme(tenantBaseTheme)
      },
    }
  }

  // If consumers provide a concrete theme object, keep that as final override.
  return {
    ...tenantOverrides,
    theme,
  }
}

/**
 * This TenantThemeProvider is when you already want to use the new setup and want the
 * tenant to be directly selectable giving the option to show multiple tenants at once.
 *
 * Our preference is to have really specific provider per tenant, this version is initially created by/for B2X team
 */
export const TenantThemeProvider = ({
  tenant,
  tenantOverrides,
  theme,
  children,
  ...rest
}: PropsWithChildren<
  ThemeProviderProps & {
    /**
     * Tenant id used by tenant-aware provider path.
     */
    tenant: TenantId

    /**
     * Optional tenant configuration overrides.
     * FIXME: why do we need this?
     */
    tenantOverrides?: Partial<TenantThemeConfig>
  }
>) => (
  <DhlTenantThemeProvider
    tenant={tenant}
    overrides={resolveTenantOverrides(tenantOverrides, theme)}
    useCssBaseline={rest.useCssBaseline}
    options={rest.options}
  >
    {children}
  </DhlTenantThemeProvider>
)
